import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import {
  ArrowDownToLineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableIcon,
  GridIcon,
} from '@revfluence/fresh-icons/solid';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { SearchInput } from '@frontend/app/refresh-components';
import { SortingState } from '@tanstack/react-table';
import { ColumnsIconV3 } from '@frontend/app/components';
import { useMessagingContext } from '@frontend/hooks';
import { useGetTableConfigurationByName, useSaveTableConfigurationMutation } from '@frontend/app/hooks';
import { logger } from '@common';
import { PostInsights } from '@frontend/gql/social/graphql';
import { ViewMode } from '../../types';
import MembersCard from '../members-card';
import ReachCard from '../reach-card';
import EngagementCard from '../engagement-card';
import ImpressionsCard from '../impressions-card';
import PostsCard from '../post-card';
import TMVCard from '../tmv-card';
import PostCardView from '../post-card-view';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
  TableHeaderPagination,
  useTablePagination,
} from '../table-header';
import { usePostTable } from '../table/hooks/usePostsTable';
import PostTable from '../table/PostTable';
import {
  POST_TABLE_COLUMN_CONFIG_VERSION,
  PostTableColumn,
  hydrateColumnsFromJson,
  defaultPostColumns,
} from '../table/config/defaultPostTableColumns';
import ColumnDialog from '../columnSelectorDialog';
import { useColumnDialog } from '../columnSelectorDialog/hooks/useColumnDialog';
import { useExportPostsCsv } from '../../hooks/useExpostPostsCsv';
import { EnrichedPostsData } from '../../hooks/usePostsData';

const { useState, useCallback, useEffect } = React;

interface PostsTabProps {
  data: EnrichedPostsData | undefined;
  isLoading: boolean;
  isPartialLoading: boolean;
  selectedColumns: PostTableColumn<PostInsights>[];
  setCurrentPage: (page: number) => void;
  currentPage: number;
  setSelectedColumns: (columns: PostTableColumn<PostInsights>[]) => void;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
  viewMode: ViewMode;
  setViewMode: (value: ViewMode) => void;
}

type ExportFormat = 'current' | 'full';
const TABLE_PAGE_SIZE = 50;

export const PostsTab = ({
  data,
  isLoading,
  isPartialLoading,
  selectedColumns,
  setSelectedColumns,
  sorting,
  setSorting,
  setCurrentPage,
  currentPage,
  viewMode,
  setViewMode,
}: PostsTabProps) => {
  const posts = data?.client?.socialAnalytics?.posts?.results || [];
  const pagination = data?.client?.socialAnalytics?.posts?.pagination;
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  const [searchText, setSearchText] = useState('');
  const [editModeColumns, setEditModeColumns] = useState<PostTableColumn<PostInsights>[]>(selectedColumns);
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [saveTableConfiguration] = useSaveTableConfigurationMutation();

  const updateGlobalFilterText = (text: string) => {
    setSearchText(text);
    setCurrentPage(0);
  };

  const { data: tableConfigData, loading: tableConfigLoading } = useGetTableConfigurationByName({
    variables: { tableName: 'social-data-posts-table' },
  });

  const { postTableProps } = usePostTable({
    selectedColumns,
    setSelectedColumns,
    data: posts,
    currentPage,
    pageSize: TABLE_PAGE_SIZE,
    globalFilter: searchText,
    handleSortingChange: setSorting,
    sorting,
    setIsPartialLoading: () => {
      /* no-op */
    }, // Empty function since we don't use partial loading for this table
  });

  const { handleExportCsv, isExporting } = useExportPostsCsv({
    postInsights: posts || [],
    selectedColumns,
  });

  const isAnyExporting = isExporting;
  const handleExport = async (format: ExportFormat) => {
    try {
      if (format === 'current') {
        handleExportCsv();
        showSuccessMessage('Current view exported successfully');
      } else {
        handleExportCsv(true);
        showSuccessMessage('Full data exported successfully');
      }
    } catch (error) {
      showErrorMessage('Failed to export data');
      logger.error('Export failed:', error);
    }
  };

  const onSaveColumnInfo = useCallback(
    async (columns: PostTableColumn<PostInsights>[]) => {
      try {
        await saveTableConfiguration({
          variables: {
            data: {
              version: POST_TABLE_COLUMN_CONFIG_VERSION,
              tableName: 'social-data-posts-table',
              columnDefinitions: columns,
            },
          },
        });
        setSelectedColumns(columns);
        setEditModeColumns(columns);
        showSuccessMessage('Columns successfully updated');
      } catch (error) {
        logger.error(error);
        showErrorMessage('error saving columns');
      }
    },
    [saveTableConfiguration, showSuccessMessage, showErrorMessage, setSelectedColumns],
  );

  useEffect(() => {
    if (
      !tableConfigLoading &&
      tableConfigData?.getTableConfigurationByName?.columnDefinitions &&
      tableConfigData?.getTableConfigurationByName?.version === POST_TABLE_COLUMN_CONFIG_VERSION
    ) {
      const newColumns = hydrateColumnsFromJson(tableConfigData.getTableConfigurationByName.columnDefinitions);
      setSelectedColumns(newColumns);
      setEditModeColumns(newColumns);
    }
  }, [tableConfigLoading, tableConfigData, setSelectedColumns]);

  const { columnDialogProps } = useColumnDialog({
    defaultSelectedColumns: defaultPostColumns,
    selectedColumns: editModeColumns,
    setSelectedColumns: setEditModeColumns,
    onSave: onSaveColumnInfo,
  });

  const { paginationProps, prevButtonProps, nextButtonProps } = useTablePagination({
    currentPage,
    totalCount: posts?.length || 0,
    pageSize: TABLE_PAGE_SIZE,
    onPageChange: setCurrentPage,
  });

  if (isLoading && !isPartialLoading) {
    return (
      <>
        <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4" aria-busy>
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading posts card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading members card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading impressions card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading engagement card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading reach card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading TMV card" />
        </section>
        {viewMode === 'table' ? (
          <Skeleton className="mx-4 mb-4 rounded-xl" aria-label="Loading posts table">
            <div className="p-4">
              <Skeleton className="h-[60px] w-full rounded-lg pb-4" />
              <div className="space-y-2 pt-4">
                {Array.from({ length: 10 }, (_, i) => (
                  <Skeleton key={`skeleton-row-${currentPage}-${i}`} className="h-[48px] w-full rounded-lg" />
                ))}
              </div>
            </div>
          </Skeleton>
        ) : (
          <Card className="mx-4 mb-4 p-4" aria-label="Loading posts cards">
            <Skeleton className="h-[60px] w-full rounded-lg mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 p-4">
              {Array.from({ length: 10 }, (_, i) => (
                <Skeleton key={`skeleton-card-${currentPage}-${i}`} className="aspect-[9/16] w-full rounded-xl" />
              ))}
            </div>
          </Card>
        )}
      </>
    );
  }
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
        <PostsCard data={totalInsights} />
        <MembersCard data={totalInsights} />
        <ImpressionsCard data={totalInsights} />
        <EngagementCard data={totalInsights} />
        <ReachCard data={totalInsights} />
        <TMVCard data={totalInsights} />
      </section>
      <Card className="mx-4 mb-4" aria-label="Members table">
        <TableHeader>
          <TableHeaderLayout>
            <TableHeaderSection>
              <TableHeaderLabel>
                <div className="flex items-center gap-4">
                  <span aria-live="polite">{paginationProps?.totalCount || 0} members</span>
                </div>
              </TableHeaderLabel>
            </TableHeaderSection>
            <TableHeaderSection>
              <SearchInput searchText={searchText} setSearchText={updateGlobalFilterText} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton data-dd-action-name="export-members-data">
                    <div className="flex items-center">
                      <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleExport('current')}
                    disabled={isAnyExporting}
                    data-dd-action-name="export-current-view"
                    aria-label="Export current view of member data"
                    className="cursor-pointer"
                  >
                    Export current view
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExport('full')}
                    disabled={isAnyExporting}
                    data-dd-action-name="export-full-data"
                    aria-label="Export all member data fields"
                  >
                    Export all fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton data-dd-action-name="open-view-selector">
                    {viewMode === 'table' ? <TableIcon className="h-4 w-4" /> : <GridIcon className="h-4 w-4" />}
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {viewMode === 'table' ? (
                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setViewMode('cards');
                        }, 150);
                      }}
                      data-dd-action-name="open-table-view"
                      aria-label="Open table view"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <GridIcon className="h-4 w-4" />
                      Cards view
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => {
                        setTimeout(() => {
                          setViewMode('table');
                        }, 150);
                      }}
                      data-dd-action-name="open-cards-view"
                      aria-label="Open cards view"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <TableIcon className="h-4 w-4" /> Table view
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <ColumnDialog {...columnDialogProps}>
                <TableHeaderIconButton
                  data-dd-action-name="open-column-selector"
                  aria-label="Select columns to display"
                  disabled={viewMode !== 'table'}
                >
                  <ColumnsIconV3 size={16} aria-hidden="true" />
                </TableHeaderIconButton>
              </ColumnDialog>
              <nav aria-label="Table navigation" className="flex items-center">
                <TableHeaderIconButton
                  {...prevButtonProps}
                  data-dd-action-name="posts-table-previous-page"
                  aria-label="Previous page"
                  aria-disabled={currentPage === 0}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                </TableHeaderIconButton>
                <TableHeaderPagination
                  {...paginationProps}
                  aria-label={`Page ${currentPage + 1} of ${Math.ceil(
                    (pagination?.totalCount || 0) / TABLE_PAGE_SIZE,
                  )}`}
                />
                <TableHeaderIconButton
                  {...nextButtonProps}
                  data-dd-action-name="posts-table-next-page"
                  aria-label="Next page"
                  aria-disabled={!pagination?.hasNextPage}
                >
                  <ChevronRightIcon aria-hidden="true" />
                </TableHeaderIconButton>
              </nav>
            </TableHeaderSection>
          </TableHeaderLayout>
        </TableHeader>
        {viewMode === 'table' ? (
          <PostTable {...postTableProps} aria-busy={isLoading} aria-label="Members data table" />
        ) : (
          <PostCardView posts={posts} currentPage={currentPage} pageSize={TABLE_PAGE_SIZE} sorting={sorting} />
        )}
      </Card>
    </>
  );
};
